import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import useRouterMonitoringSignalR from '@/hooks/useRouterMonitoringSignalR';
import '@/styles/ITReportStyles.scss';
import { formatDateTime, parseUtcMs } from '@/utils/formatDate';
import { 
  FaNetworkWired, 
  FaSync, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaTimesCircle,
  FaServer,
  FaMicrochip,
  FaMemory,
  FaClock,
  FaChartLine,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaProjectDiagram
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Routers Overview Page
 * Displays all MikroTik routers with API connectivity status and high-level statistics
 * Provides centralized management interface for router configuration
 */
const RoutersOverviewPage = () => {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [routers, setRouters] = useState([]);
  const [routerStats, setRouterStats] = useState({});
  const [monitoringStatus, setMonitoringStatus] = useState({});
  const [filter, setFilter] = useState('all'); // all, connected, disconnected, configured
  const [clusterMembership, setClusterMembership] = useState({}); // routerId → cluster info
  const routersRef = useRef([]);

  /**
   * Handle real-time router availability changes from SignalR.
   * Updates stats and monitoring status in-place for instant UI feedback,
   * then re-fetches the affected router's data for full accuracy.
   */
  const handleRouterAvailabilityChanged = useCallback(async (data) => {
    const { routerId, isAvailable, error: availError } = data;

    // Immediately update the routerStats map so the card border/badge flips
    setRouterStats(prev => ({
      ...prev,
      [routerId]: {
        ...prev[routerId],
        connected: isAvailable,
        error: isAvailable ? undefined : (availError || prev[routerId]?.error)
      }
    }));

    // Update monitoring status availability
    setMonitoringStatus(prev => {
      if (!prev[routerId]) return prev;
      return {
        ...prev,
        [routerId]: {
          ...prev[routerId],
          isAvailable,
          lastError: isAvailable ? null : (availError || prev[routerId]?.lastError)
        }
      };
    });

    // Re-fetch full stats and monitoring status for this router in the background
    try {
      const [statsRes, monRes] = await Promise.all([
        apiFetch(`/api/RouterConfig/${routerId}/stats`),
        apiFetch(`/api/RouterConfig/${routerId}/monitoring-status`)
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.data) {
          setRouterStats(prev => ({ ...prev, [routerId]: { id: routerId, ...statsData.data } }));
        }
      }

      if (monRes.ok) {
        const monData = await monRes.json();
        if (monData.data) {
          setMonitoringStatus(prev => ({ ...prev, [routerId]: { id: routerId, ...monData.data } }));
        }
      }
    } catch (catchError) {
        Sentry.captureException(catchError);
      // Background refresh failed — the instant update above keeps the UI consistent
    }
  }, [apiFetch]);

  // SignalR: subscribe to all-routers group for real-time availability changes
  useRouterMonitoringSignalR({
    onRouterAvailabilityChanged: handleRouterAvailabilityChanged,
    enabled: true
  });

  useEffect(() => {
    loadRouters();
  }, []);

  /**
   * Loads all configured routers with their current status.
   *
   * Only the router list and cached monitoring status (maintained by the
   * background RouterMonitoringService) are loaded on initial page open.
   * Live RouterOS API statistics (CPU, memory, uptime) are intentionally
   * NOT fetched during initial load to avoid individual TCP connections to
   * every router — the background monitoring service already knows which
   * routers are online and maintains that state in real-time via SignalR.
   *
   * Stats are still refreshed periodically by the 30-second background
   * interval, and the user can manually trigger a refresh via the button.
   */
  const loadRouters = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get enabled router configurations from database
      const response = await apiFetch('/api/RouterConfig/enabled');
      if (!response.ok) {
        throw new Error('Failed to load router configurations');
      }

      const data = await response.json();
      const configuredRouters = data.success ? data.data : [];
      setRouters(configuredRouters);
      routersRef.current = configuredRouters;

      // Page is ready to render — clear loading state before fetching live data
      setLoading(false);

      // Load monitoring status from the background service cache.
      // This is fast (reads from database) and reflects the current known state
      // without opening individual TCP connections to each router.
      loadMonitoringStatus(configuredRouters).catch(() => {});

      // Load VRRP cluster membership map so clustered routers can be grouped
      loadClusterMembership().catch(() => {});

      // Router statistics (CPU, memory, uptime) require live RouterOS API
      // connections and are therefore deferred to the background refresh cycle
      // to keep the initial page load snappy. The Monitoring Statistics section
      // will still appear immediately using the monitoring status data above.
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading routers:', err);
      setError(err.message || 'Failed to load routers');
      setLoading(false);
    }
  };

  /**
   * Loads real-time statistics from each router via RouterOS API
   */
  const loadRouterStatistics = async (routerList) => {
    const statsPromises = routerList.map(async (router) => {
      try {
        const response = await apiFetch(`/api/RouterConfig/${router.id}/stats`);
        if (!response.ok) {
          return { id: router.id, connected: false, error: 'Failed to fetch stats' };
        }

        const data = await response.json();
        return { id: router.id, ...data.data };
      } catch (err) {
          Sentry.captureException(err);
        return { id: router.id, connected: false, error: err.message };
      }
    });

    const stats = await Promise.all(statsPromises);
    const statsMap = {};
    stats.forEach(stat => {
      statsMap[stat.id] = stat;
    });
    setRouterStats(statsMap);
  };

  /**
   * Loads monitoring status for each router
   */
  const loadMonitoringStatus = async (routerList) => {
    const statusPromises = routerList.map(async (router) => {
      try {
        const response = await apiFetch(`/api/RouterConfig/${router.id}/monitoring-status`);
        if (!response.ok) {
          return { id: router.id, enabled: false };
        }

        const data = await response.json();
        return { id: router.id, ...data.data };
      } catch (catchError) {
          Sentry.captureException(catchError);
        return { id: router.id, enabled: false };
      }
    });

    const statuses = await Promise.all(statusPromises);
    const statusMap = {};
    statuses.forEach(status => {
      statusMap[status.id] = status;
    });
    setMonitoringStatus(statusMap);
  };

  /**
   * Loads VRRP cluster membership data. Returns a map of routerConfigId → cluster info
   * so the overview page can group clustered routers into single cards.
   */
  const loadClusterMembership = async () => {
    try {
      const response = await apiFetch('/api/RouterConfig/cluster-membership');
      if (!response.ok) return;
      const data = await response.json();
      if (data.success && data.data) {
        setClusterMembership(data.data);
      }
    } catch (err) {
        Sentry.captureException(err);
      // Non-critical — clustered routers will just appear as individual cards
      console.warn('Failed to load cluster membership:', err?.message || err);
    }
  };

  // Background auto-refresh: silently update stats and monitoring for all routers
  useEffect(() => {
    const interval = setInterval(async () => {
      const currentRouters = routersRef.current;
      if (currentRouters.length === 0) return;

      try {
        await Promise.all([
          loadRouterStatistics(currentRouters),
          loadMonitoringStatus(currentRouters)
        ]);
      } catch (catchError) {
          Sentry.captureException(catchError);
        // Silent refresh — don't disrupt the UI on failure
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [apiFetch]);

  /**
   * Navigates to router configuration page
   */
  const handleConfigureRouter = (routerId) => {
    navigate(`/it/config-management/routers/${routerId}`);
  };

  /**
   * Formats uptime from seconds to human-readable format
   */
  const formatUptime = (seconds) => {
    if (!seconds) return 'Unknown';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  /**
   * Formats bytes to human-readable size
   */
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Returns `{ icon, color, text, badgeBackground }` for the status badge.
   *
   * `badgeBackground` is the light tinted background that complements `color` so
   * both the card border and the badge chip are always styled consistently from
   * the same data source — the badge must never rely on `stats.connected` (which
   * is undefined on initial load) independently of `color`.
   *
   * Priority of status sources:
   *  1. ICMP monitoring status from the background service (always available
   *     immediately after page load without touching the router API).
   *  2. RouterOS API stats (connected/disconnected) — only used as an override
   *     when stats have been loaded via the background refresh cycle.
   *  3. Falls back to "Unknown" only when monitoring is disabled AND no stats
   *     have been loaded yet.
   */
  const getStatusIndicator = (router) => {
    const monStatus = monitoringStatus[router.id];
    const stats = routerStats[router.id];

    // RouterOS API stats take priority ONLY when the API successfully connected.
    // A successful API connection is a definitive "router is up and API is reachable" signal.
    if (stats?.connected) {
      return { icon: <FaCheckCircle />, color: '#4caf50', text: 'Connected', badgeBackground: '#e7f5e7' };
    }

    // If monitoring is active, use it as the primary source of truth for whether the
    // router itself is reachable. A failed RouterOS API connection (stats.connected = false)
    // may simply mean the API credentials are wrong or the API port is closed — it does
    // NOT mean the router is offline, so we must not override monitoring status with it.
    if (monStatus && monStatus.isMonitoring) {
      const isStale = isMonitoringStale(monStatus);
      if (!isStale && monStatus.isAvailable) {
        return { icon: <FaCheckCircle />, color: '#4caf50', text: 'Online', badgeBackground: '#e7f5e7' };
      }
      if (!isStale && !monStatus.isAvailable) {
        return { icon: <FaTimesCircle />, color: '#f44336', text: 'Offline', badgeBackground: '#fee' };
      }
      // Monitoring data is older than 5 minutes — service may have stopped
      return { icon: <FaExclamationTriangle />, color: '#ff9800', text: 'Stale Data', badgeBackground: '#fff8e1' };
    }

    // Stats loaded with a failed API connection and monitoring is disabled —
    // the RouterOS API is genuinely unreachable with no other signal.
    if (stats && !stats.connected) {
      return { icon: <FaTimesCircle />, color: '#f44336', text: 'Disconnected', badgeBackground: '#fee' };
    }

    // No stats and no monitoring data — genuinely unknown state
    return { icon: <FaExclamationTriangle />, color: '#999', text: 'Unknown', badgeBackground: '#f5f5f5' };
  };

  /**
   * Determines whether a router should be considered online.
   * A router is online when:
   *  1. ICMP monitoring reports it as available
   *  2. The monitoring data is fresh (lastIcmpSuccess or lastSnmpSuccess within 5 minutes)
   *  3. The RouterOS API connection succeeded (if stats have loaded)
   * This prevents stale monitoring data from masking offline routers,
   * and prevents a router that responds to ping but has a broken API
   * from being counted as online.
   *
   * NOTE: lastUpdated is NOT used for freshness — it tracks config changes,
   * not poll activity. Poll freshness is tracked via lastIcmpSuccess / lastSnmpSuccess.
   */
  const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

  const isMonitoringStale = (monStatus) => {
    // Use the most recent successful poll timestamp for freshness detection.
    // lastUpdated is set on config changes — NOT on every monitoring poll —
    // so it must not be used here (it would make healthy routers appear stale).
    // parseUtcMs normalises backend DateTime strings (which lack 'Z' because
    // EF Core returns Kind=Unspecified) so they are correctly treated as UTC.
    const IcmpTime = parseUtcMs(monStatus?.lastIcmpSuccess);
    const SnmpTime = parseUtcMs(monStatus?.lastSnmpSuccess);

    // Use the most recent of the two poll timestamps as the freshness indicator.
    // If neither is available, the monitoring service has never polled — treat as stale.
    const MostRecentPollTime = (IcmpTime !== null && SnmpTime !== null)
      ? Math.max(IcmpTime, SnmpTime)
      : (IcmpTime ?? SnmpTime);

    if (MostRecentPollTime === null) return true;
    return (Date.now() - MostRecentPollTime) > STALE_THRESHOLD_MS;
  };

  const isRouterOnline = (router) => {
    const monStatus = monitoringStatus[router.id];
    const stats = routerStats[router.id];

    // RouterOS API successfully connected — the router is definitively online,
    // regardless of monitoring staleness (consistent with getStatusIndicator).
    if (stats?.connected) return true;

    // ICMP monitoring must report available
    if (!monStatus?.isAvailable) return false;

    // If monitoring data is stale (service stopped updating), don't trust isAvailable
    if (isMonitoringStale(monStatus)) return false;

    return true;
  };

  const getFilteredRouters = () => {
    if (filter === 'all') return routers;

    return routers.filter(router => {
      if (filter === 'online') return isRouterOnline(router);
      if (filter === 'offline') return !isRouterOnline(router);
      if (filter === 'managed') return router.provisioningStatus === 'Provisioned';

      return true;
    });
  };

  if (loading) {
    return (
      <div className="main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <HexagonLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content" style={{ padding: '20px' }}>
        <div className="error-message" style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33' }}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={loadRouters} style={{ marginTop: '10px', padding: '8px 16px', cursor: 'pointer' }}>
            <FaSync /> Retry
          </button>
        </div>
      </div>
    );
  }

  const filteredRouters = getFilteredRouters();
  const onlineCount = routers.filter(r => isRouterOnline(r)).length;
  const offlineCount = routers.length - onlineCount;
  const managedCount = routers.filter(r => r.provisioningStatus === 'Provisioned').length;

  /**
   * Build display items: clustered routers are grouped into a single "cluster card"
   * while unclustered routers appear as individual cards. This prevents duplicate
   * cards for routers that belong to the same VRRP failover group.
   */
  const buildDisplayItems = () => {
    const seenClusterIds = new Set();
    const items = [];

    // Build a O(1) lookup map once so that member-router resolution inside
    // the loop is O(1) per lookup instead of O(n) — avoids an O(n²) total.
    const routerById = new Map(routers.map(r => [r.id, r]));

    filteredRouters.forEach(router => {
      const membership = clusterMembership[router.id];
      if (membership && membership.clusterId) {
        // This router belongs to a cluster — show as a cluster card (once)
        if (!seenClusterIds.has(membership.clusterId)) {
          seenClusterIds.add(membership.clusterId);
          // Gather all member routers using the O(1) map lookup.
          // Intentionally resolves from the full `routers` list so that the
          // cluster card always shows all members even when some are hidden by
          // the active filter (consistent with the previous behaviour).
          const memberRouters = (membership.memberRouterIds || [])
            .map(id => routerById.get(id))
            .filter(Boolean);
          items.push({
            type: 'cluster',
            clusterId: membership.clusterId,
            clusterName: membership.clusterName,
            clusterStatus: membership.clusterStatus,
            virtualIpAddress: membership.virtualIpAddress,
            totalMembers: membership.totalMembers,
            memberRouters,
            // Use the primary router as the "representative" for status
            primaryRouter: memberRouters[0] || router,
            membership
          });
        }
      } else {
        // Unclustered router — show as individual card
        items.push({ type: 'router', router });
      }
    });

    return items;
  };

  const displayItems = buildDisplayItems();

  return (
    <div className="main-content" style={{ padding: '20px', maxWidth: '100%', boxSizing: 'border-box' }}>
      <div className="routers-header" style={{ marginBottom: '30px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 10px 0' }}>
            <FaServer /> MikroTik Router Management
          </h1>
          <p style={{ color: '#666', margin: 0 }}>
            Centralized management and monitoring for all MikroTik routers
          </p>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', border: '2px solid #e0e0e0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
              <FaNetworkWired color="#2196f3" size={24} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Total Routers</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{routers.length}</div>
          </div>

          <div style={{ background: '#e7f5e7', padding: '15px', borderRadius: '8px', border: '2px solid #4caf50' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
              <FaCheckCircle color="#4caf50" size={24} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Online</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2e7d32' }}>{onlineCount}</div>
          </div>

          <div style={{ background: '#fee', padding: '15px', borderRadius: '8px', border: '2px solid #f44336' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
              <FaTimesCircle color="#f44336" size={24} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Offline</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c33' }}>{offlineCount}</div>
          </div>

          <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: '8px', border: '2px solid #2196f3' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
              <FaShieldAlt color="#2196f3" size={24} />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Managed</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1565c0' }}>{managedCount}</div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              padding: '8px 16px',
              background: filter === 'all' ? '#2196f3' : 'white',
              color: filter === 'all' ? 'white' : '#333',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            All ({routers.length})
          </button>
          <button
            onClick={() => setFilter('online')}
            style={{
              padding: '8px 16px',
              background: filter === 'online' ? '#4caf50' : 'white',
              color: filter === 'online' ? 'white' : '#333',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Online ({onlineCount})
          </button>
          <button
            onClick={() => setFilter('offline')}
            style={{
              padding: '8px 16px',
              background: filter === 'offline' ? '#f44336' : 'white',
              color: filter === 'offline' ? 'white' : '#333',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Offline ({offlineCount})
          </button>
          <button
            onClick={() => setFilter('managed')}
            style={{
              padding: '8px 16px',
              background: filter === 'managed' ? '#2196f3' : 'white',
              color: filter === 'managed' ? 'white' : '#333',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Managed ({managedCount})
          </button>
        </div>
      </div>

      {/* Routers Grid */}
      {displayItems.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: '#f9f9f9', borderRadius: '8px' }}>
          <FaExclamationTriangle size={48} color="#ff9800" />
          <h3>No Routers Found</h3>
          <p>
            {filter !== 'all' 
              ? `No routers matching filter "${filter}"` 
              : 'No enabled routers configured. Please configure router credentials in Settings.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
          {displayItems.map(item => {
            if (item.type === 'cluster') {
              // ── Cluster Card: shows all member routers as a single grouped card ──
              const clusterStatusColor = item.clusterStatus === 'Active' ? '#4caf50'
                : item.clusterStatus === 'Degraded' ? '#ff9800'
                : item.clusterStatus === 'Offline' ? '#f44336' : '#999';
              const clusterStatusBg = item.clusterStatus === 'Active' ? '#e7f5e7'
                : item.clusterStatus === 'Degraded' ? '#fff8e1'
                : item.clusterStatus === 'Offline' ? '#fee' : '#f5f5f5';

              return (
                <div
                  key={`cluster-${item.clusterId}`}
                  style={{
                    background: 'white',
                    border: `2px solid ${clusterStatusColor}`,
                    borderRadius: '8px',
                    padding: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Cluster Header */}
                  <div style={{ marginBottom: '12px' }}>
                    <h3 style={{ margin: '0 0 5px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FaProjectDiagram color={clusterStatusColor} />
                      {item.clusterName}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '3px 8px', background: clusterStatusBg, color: clusterStatusColor,
                        borderRadius: '10px', fontSize: '0.8rem', fontWeight: 'bold'
                      }}>
                        <FaCheckCircle /> {item.clusterStatus}
                      </span>
                      <span style={{ color: '#666', fontSize: '0.85rem' }}>
                        VIP: {item.virtualIpAddress}
                      </span>
                      <span style={{ color: '#999', fontSize: '0.8rem' }}>
                        {item.totalMembers} member{item.totalMembers !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Cluster Members List */}
                  <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
                    {item.memberRouters.map(memberRouter => {
                      const memberStatus = getStatusIndicator(memberRouter);
                      const memberClusterInfo = clusterMembership[memberRouter.id];
                      const isMaster = memberClusterInfo?.isCurrentMaster;
                      const role = memberClusterInfo?.role || 'Member';
                      const stats = routerStats[memberRouter.id] || {};

                      return (
                        <div
                          key={memberRouter.id}
                          onClick={() => handleConfigureRouter(memberRouter.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '8px 10px', marginBottom: '4px',
                            background: isMaster ? 'rgba(76, 175, 80, 0.06)' : '#fafafa',
                            borderRadius: '6px', cursor: 'pointer',
                            border: isMaster ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid #eee',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = isMaster ? 'rgba(76, 175, 80, 0.12)' : '#f0f0f0'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = isMaster ? 'rgba(76, 175, 80, 0.06)' : '#fafafa'; }}
                        >
                          {/* Role badge */}
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem',
                            fontWeight: 'bold', minWidth: 65, textAlign: 'center',
                            background: role === 'Primary' ? '#e3f2fd' : role === 'Secondary' ? '#fff3e0' : '#f5f5f5',
                            color: role === 'Primary' ? '#1565c0' : role === 'Secondary' ? '#e65100' : '#666'
                          }}>
                            {role}
                          </span>

                          {/* Router name and IP */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {memberRouter.displayName}
                            </div>
                            <div style={{ color: '#999', fontSize: '0.8rem' }}>{memberRouter.ipAddress}</div>
                          </div>

                          {/* VRRP State badge */}
                          {isMaster ? (
                            <span style={{
                              padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem',
                              fontWeight: 'bold', background: '#e7f5e7', color: '#2e7d32'
                            }}>
                              <FaCheckCircle style={{ marginRight: 3, fontSize: '0.7rem' }} /> Active
                            </span>
                          ) : (
                            <span style={{
                              padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem',
                              background: '#f5f5f5', color: '#999'
                            }}>
                              Standby
                            </span>
                          )}

                          {/* Status dot */}
                          <span style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: memberStatus.color, flexShrink: 0
                          }} title={memberStatus.text} />

                          {/* CPU if available */}
                          {stats.connected && stats.cpuLoad !== undefined && (
                            <span style={{ fontSize: '0.75rem', color: '#666', minWidth: 40 }}>
                              <FaMicrochip style={{ marginRight: 2 }} />{stats.cpuLoad}%
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // ── Individual Router Card (unclustered) ──
            const router = item.router;
            const status = getStatusIndicator(router);
            const stats = routerStats[router.id] || {};

            return (
              <div
                key={router.id}
                onClick={() => handleConfigureRouter(router.id)}
                style={{
                  background: 'white',
                  border: `2px solid ${status.color}`,
                  borderRadius: '8px',
                  padding: '20px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                {/* Router Header */}
                <div style={{ marginBottom: '15px' }}>
                  <h3 style={{ margin: '0 0 5px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaNetworkWired color={status.color} />
                    {router.displayName}
                  </h3>
                  <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>
                    {router.ipAddress}:{router.apiPort}
                  </p>
                </div>

                {/* Status Badge — background derived from `status.badgeBackground` so it is
                    always consistent with the border colour and status text, even before
                    live RouterOS API stats have loaded. */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 10px',
                    background: status.badgeBackground,
                    color: status.color,
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}
                >
                  {status.icon} {status.text}
                </div>

                {/* Monitoring Status Badge */}
                {(() => {
                  const monStatus = monitoringStatus[router.id];
                  if (!monStatus || !monStatus.isMonitoring) {
                    return (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '5px 10px',
                          background: '#f5f5f5',
                          color: '#999',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          marginBottom: '15px',
                          marginLeft: '10px'
                        }}
                      >
                        <FaEyeSlash /> Monitoring Disabled
                      </div>
                    );
                  }

                  const stale = isMonitoringStale(monStatus);
                  const isOnline = !stale && monStatus.isAvailable;
                  const bgColor = stale ? '#fff8e1' : isOnline ? '#e3f2fd' : '#fee';
                  const textColor = stale ? '#f57f17' : isOnline ? '#1976d2' : '#d32f2f';
                  const icon = stale ? <FaExclamationTriangle /> : isOnline ? <FaEye /> : <FaExclamationTriangle />;
                  const text = stale ? 'Monitoring - Stale Data' : isOnline ? 'Monitoring Active - Online' : 'Monitoring Active - Offline';

                  return (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 10px',
                        background: bgColor,
                        color: textColor,
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        marginBottom: '15px',
                        marginLeft: '10px'
                      }}
                    >
                      {icon} {text}
                    </div>
                  );
                })()}

                {/* Router Stats */}
                {stats.connected && (
                  <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.9rem' }}>
                      <div>
                        <div style={{ color: '#666', marginBottom: '2px' }}>
                          <FaServer style={{ marginRight: '5px' }} />
                          RouterOS
                        </div>
                        <div style={{ fontWeight: 'bold' }}>{stats.version || router.routerOSVersion || 'Unknown'}</div>
                      </div>
                      
                      <div>
                        <div style={{ color: '#666', marginBottom: '2px' }}>
                          <FaClock style={{ marginRight: '5px' }} />
                          Uptime
                        </div>
                        <div style={{ fontWeight: 'bold' }}>{formatUptime(stats.uptime)}</div>
                      </div>

                      <div>
                        <div style={{ color: '#666', marginBottom: '2px' }}>
                          <FaMicrochip style={{ marginRight: '5px' }} />
                          CPU Usage
                        </div>
                        <div style={{ fontWeight: 'bold' }}>
                          {stats.cpuLoad !== undefined ? `${stats.cpuLoad}%` : 'N/A'}
                        </div>
                      </div>

                      <div>
                        <div style={{ color: '#666', marginBottom: '2px' }}>
                          <FaMemory style={{ marginRight: '5px' }} />
                          Memory
                        </div>
                        <div style={{ fontWeight: 'bold' }}>
                          {stats.memoryUsage !== undefined ? `${stats.memoryUsage}%` : 'N/A'}
                        </div>
                      </div>

                      {stats.interfaceCount !== undefined && (
                        <div>
                          <div style={{ color: '#666', marginBottom: '2px' }}>
                            <FaChartLine style={{ marginRight: '5px' }} />
                            Interfaces
                          </div>
                          <div style={{ fontWeight: 'bold' }}>{stats.interfaceCount}</div>
                        </div>
                      )}

                      {stats.freeMemory !== undefined && (
                        <div>
                          <div style={{ color: '#666', marginBottom: '2px' }}>Free Memory</div>
                          <div style={{ fontWeight: 'bold' }}>{formatBytes(stats.freeMemory)}</div>
                        </div>
                      )}
                    </div>

                    {stats.lastChecked && (
                      <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#999', textAlign: 'right' }}>
                        Last checked: {formatDateTime(stats.lastChecked)}
                      </div>
                    )}
                  </div>
                )}

                {/* Monitoring Stats (if available and enabled) */}
                {monitoringStatus[router.id]?.isMonitoring && (
                  <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666' }}>
                      <FaEye style={{ marginRight: '5px' }} />
                      Monitoring Statistics (24h)
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.85rem' }}>
                      {monitoringStatus[router.id].lastIcmpSuccess && (
                        <div>
                          <div style={{ color: '#666', marginBottom: '2px' }}>Last Seen</div>
                          <div style={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                            {formatDateTime(monitoringStatus[router.id].lastIcmpSuccess)}
                          </div>
                        </div>
                      )}
                      {monitoringStatus[router.id].icmpIntervalMs && (
                        <div>
                          <div style={{ color: '#666', marginBottom: '2px' }}>Ping Interval</div>
                          <div style={{ fontWeight: 'bold' }}>{Math.round(monitoringStatus[router.id].icmpIntervalMs / 1000)}s</div>
                        </div>
                      )}
                      {monitoringStatus[router.id].lastError && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ color: '#d32f2f', fontSize: '0.8rem', padding: '5px', background: '#fee', borderRadius: '4px' }}>
                            <FaExclamationTriangle style={{ marginRight: '5px' }} />
                            {monitoringStatus[router.id].lastError}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {!stats.connected && stats.error && (
                  <div style={{ marginTop: '10px', padding: '10px', background: '#fee', borderRadius: '4px', fontSize: '0.85rem', color: '#c33' }}>
                    <FaExclamationTriangle style={{ marginRight: '5px' }} />
                    {stats.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Help Section */}
      <div style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
        <h3>About Router Management</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginTop: '15px' }}>
          <div>
            <h4>Current Features:</h4>
            <ul style={{ paddingLeft: '20px' }}>
              <li>Real-time router connectivity monitoring with ICMP and SNMP v3</li>
              <li>Router resource statistics (CPU, Memory, Uptime, Interfaces)</li>
              <li>Centralized configuration management with safe mode protection</li>
              <li>Secure credential storage with AES-256 encryption</li>
              <li>Automatic and manual backup and restore with version history</li>
              <li>Bulk configuration deployment across multiple routers</li>
              <li>Scheduled script execution using cron expressions</li>
              <li>Unified firewall management with IP blacklisting</li>
              <li>LLM-based configuration validation and recommendations</li>
              <li>RADIUS authentication and SNMP provisioning automation</li>
              <li>Real-time monitoring with SignalR updates and alerting</li>
              <li>VRRP cluster management with automated failover configuration</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoutersOverviewPage;
